import {
  DOWN_TIME_REASONS,
  mutateDownTimeReasons,
} from "@/api/downtimeReasons/useDowntimeReasonsQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { OperationAutoComplete } from "@/features/ui/autoComplete/operation/operation-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  DefaultApiDowntimeReasonsDowntimeReasonCodePutRequest,
  DowntimeReasonsGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
});

export const DowntimeReasonsUpdateForm = (params: {
  formatterProps: DowntimeReasonsGet200ResponseRowsInner;
}) => {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: updateMutate } = useMutation(
    (params: DefaultApiDowntimeReasonsDowntimeReasonCodePutRequest) =>
      mutateDownTimeReasons
        .update(params)
        .mutationFn(params as DefaultApiDowntimeReasonsDowntimeReasonCodePutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([DOWN_TIME_REASONS]);
        closeModal(formatterProps);
        customAlert(
          `${t("message.editSuccess", { item: `${t("downtimeReason.downtimeReason")}` })}`,
          `${t("common.editSuccess")}`,
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("downtimeReason.code") }),
          `${t("common.editFail", { item: `${t("downtimeReason.downtimeReason")}` })}`,
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      code: formatterProps.code,
      name: formatterProps.name,
      operationCode: formatterProps.operationCode,
    },
  });

  const onSubmit = () => {
    try {
      updateMutate({
        downtimeReasonCode: formatterProps.code!,
        downtimeReasonsGetRequest: {
          code: form.values.code!,
          name: form.values.name!,
          operationCode: form.values.operationCode!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("downtimeReason.code") }),
        `${t("common.editFail", { item: `${t("downtimeReason.downtimeReason")}` })}`,
        "red",
      );
    }
  };

  return (
    <Update.Wrapper>
      <Update.Input
        label={t("downtimeReason.code")}
        defaultValue={formatterProps?.code}
        placeholder={t("placeholder.input", { item: `${t("downtimeReason.code")}` })}
        {...form.getInputProps("code")}
        disabled
      />
      <Update.Input
        label={t("downtimeReason.name")}
        defaultValue={formatterProps?.name}
        placeholder={t("placeholder.input", { item: `${t("downtimeReason.name")}` })}
        {...form.getInputProps("name")}
        error={form.isDirty("name") ? t("common.toSave") : ""}
        styles={{
          input: {
            color: form.isDirty("name") ? "red !important" : "",
            borderColor: form.isDirty("name") ? "red !important" : "",
          },
          error: { fontSize: 11, color: "red" },
        }}
      />
      <OperationAutoComplete
        label={t("operation.code")}
        defaultValue={formatterProps?.operationCode}
        placeholder={t("placeholder.select", { item: `${t("operation.code")}` })}
        {...form.getInputProps("operationCode")}
        error={form.isDirty("operationCode") ? t("common.toSave") : ""}
      />
      <Update.ButtonBox>
        <Update.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Update.Button>
        <Update.Button
          color="blue"
          disabled={validateFormValues({
            pageType: "downtimeReasons",
            values: form.values,
          })}
          onClick={onSubmit}
        >
          {t("common.edit", { item: `${t("downtimeReason.downtimeReason")}` })}
        </Update.Button>
      </Update.ButtonBox>
    </Update.Wrapper>
  );
};
